<script setup lang="ts">
import { computed, ref } from 'vue'
import { MarButton, MarDialog, MarForm, MarTextareaInput, MarTextInput, notify } from '@nimling/components-nimtech'
import { useAnnotationsStore } from '@/stores/annotations'

const props = defineProps<{ pageViewId: string; viewName: string; time: string }>()
const emit = defineEmits<{ close: [result?: 'saved'] }>()

const annotationsStore = useAnnotationsStore()

const title = ref('')
const text = ref('')
const saving = ref(false)

const at = computed(() => new Date(props.time).toLocaleString())

const postAnnotation = async () => {
  saving.value = true
  try {
    await annotationsStore.postAnnotation({
      page_view_id: props.pageViewId,
      time: props.time,
      title: title.value.trim(),
      text: text.value.trim() || null,
    })
    emit('close', 'saved')
  } catch (err) {
    notify('error', err instanceof Error ? err.message : 'Annotation save failed')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <MarDialog :title="`Annotate ${at}`" @close="emit('close')">
    <div class="annotation-form">
      <p class="annotation-hint">An annotation at this moment on {{ viewName }}, drawn by the view wherever this placement is shown and listed under Annotations.</p>
      <MarForm>
        <MarTextInput v-model="title" label="Title" placeholder="What happened" autofocus @keydown.enter="title.trim() && postAnnotation()" />
        <MarTextareaInput v-model="text" label="Text" :rows="2" placeholder="Optional detail" />
      </MarForm>
    </div>

    <template #footer>
      <MarButton label="Cancel" severity="secondary" tooltip="Close without an annotation" @click="emit('close')" />
      <MarButton label="Annotate" tooltip="Save the annotation" :disabled="!title.trim()" :loading="saving" @click="postAnnotation" />
    </template>
  </MarDialog>
</template>

<style scoped>
.annotation-form {
  width: min(32em, 90vw);
  display: flex;
  flex-direction: column;
  gap: 1em;
}

.annotation-hint {
  margin: 0;
  font-size: 0.8125em;
  color: var(--mar-text-muted-color);
}
</style>
